import { useState } from "react";
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import DateRange from '../selectComponents/DateRange';
import DateFilter from '../selectComponents/DateFilter';
import HourFilter from '../selectComponents/HourFilter';
import OutputType from '../selectComponents/OutputType';
import InfoButton from '../selectComponents/InfoButton';
import { FieldsetBox, LegendStyled } from '../styling/StyledComponents';

const directionChoices = [
  {value: 36, label: '36-point compass'},
  {value: 16, label: '16-point compass',},
]
const pctcntChoices = [
  {value: 'percent', label: 'Percent'},
  {value: 'counts', label: 'Total Counts',},
]
const wsunitsChoices = [
  {value: 'miles/hr', label: 'Miles/hour'},
  {value: 'kts', label: 'Knots',},
  {value: 'm/s', label: 'Meters/second',},
]

const beaufort_bins = [0, 1.3, 4, 8, 13, 19, 25, 32, 39, 47]

export default function WindroseOptions(props) {
  const { userParams, setUserParams, selectOptions, handleChange, setDisableGoButton } = props;
  const [ shortcutAnchorEl, setShortcutAnchorEl ] = useState();
  const [ speedbinsError, setSpeedbinsError ] = useState(Array(10).fill(false));
  
  const handleSpeedChange = (index) => event => {
    const sbins = userParams.speedbins;
    const sbinserror = []
    const newbin = event.target.value ? event.target.value : "";
    sbins[index] = (newbin !== "" && !isNaN(newbin)) ? parseInt(newbin, 10) : "";
    sbins.forEach((sb, i) => {
      const sb_previous = i === 0 ? -999 : sbins[i-1];
      const sb_next = i === sbins.length-1 ? 9999 : sbins[i+1];
      if (sb_next !== "" && sb_previous !== "" && sb !== "") {
        sbinserror.push(sb <= sb_previous || sb >= sb_next);
      }
    })
    setUserParams({ ...userParams, speedbins: sbins});
    setSpeedbinsError(sbinserror);
    setDisableGoButton(sbinserror.indexOf(true) >= 0 ? true : false);
  }

  const handleShortcutClose = (index) => {
    setShortcutAnchorEl(null);
    if (index === 0) {
        const speedbins = beaufort_bins;
        const wsunits = 'miles/hr';
        setUserParams({...userParams, speedbins, wsunits });
    } else if (index) {
      const newspeedbins = [ ...Array(10).keys() ].map( i => i * index);
      setUserParams({...userParams, speedbins: newspeedbins});
    }
    setSpeedbinsError(Array(10).fill(false));
  }

  return (
    <>
      <DateRange 
        userParams={userParams}
        setUserParams={setUserParams}
      />
      <DateFilter
        userParams={userParams}
        setUserParams={setUserParams}
        selectOptions={selectOptions}
        handleChange={handleChange}
      />
      <HourFilter 
        userParams={userParams}
        selectOptions={selectOptions}
        handleChange={handleChange}
      />
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <TextField
          select
          name="pctcnt"
          label="Summary type"
          value={userParams.pctcnt}
          onChange={handleChange}
          InputProps={{endAdornment: <InfoButton infoFor="pctcnt" />}}
          sx={{ "& .MuiSelect-icon": { right: '28px' } }}
        >
          {selectOptions(pctcntChoices)}
        </TextField>
        <TextField
          select
          name="directbincnt"
          label="Wind direction bins"
          value={userParams.directbincnt}
          onChange={(e) => handleChange(e,'integer')}
          InputProps={{endAdornment: <InfoButton infoFor="directbincnt" />}}
          sx={{ "& .MuiSelect-icon": { right: '28px' } }}
        >
          {selectOptions(directionChoices)}
        </TextField>
        <TextField
          select
          name="wsunits"
          label="Wind speed units"
          value={userParams.wsunits}
          onChange={handleChange}
        >
          {selectOptions(wsunitsChoices)}
        </TextField>
      </Stack>

      <FieldsetBox component="fieldset" sx={{ mt: 1 }}>
        <LegendStyled>Wind speed bins ({userParams.wsunits})</LegendStyled>
        <Button
          onClick={(e) => setShortcutAnchorEl(e.currentTarget)}
          sx={{ mr: 1 }}
        >
          Shortcuts
        </Button>
        <Menu 
          anchorEl={shortcutAnchorEl} 
          open={Boolean(shortcutAnchorEl)} 
          onClose={(event) => handleShortcutClose()}
        >
          <MenuItem onClick={(event) => handleShortcutClose(5)}>Even increments of 5</MenuItem>
          <MenuItem onClick={(event) => handleShortcutClose(4)}>Even increments of 4</MenuItem>
          <MenuItem onClick={(event) => handleShortcutClose(3)}>Even increments of 3</MenuItem>
          <MenuItem onClick={(event) => handleShortcutClose(2)}>Even increments of 2</MenuItem>
          <MenuItem onClick={(event) => handleShortcutClose(0)}>Beaufort scale (mph)</MenuItem>
        </Menu>
        {userParams.speedbins.slice(1).map((ws, k) => 
          <TextField
            key={k}
            value={ws}
            onChange={handleSpeedChange(k+1)}
            sx={{ width: "5ch",  mt: 0, mb: 0, ml: 1 }} 
            error={speedbinsError[k+1]}
            inputProps={{sx: { p: "0.25em"}}}
          />
        )}
      </FieldsetBox>
      <br />
      <OutputType
        userParams={userParams}
        setUserParams={setUserParams}
      />
    </>
  )
}
